export type ServerFieldToFormikOptions = {
  /** `formPageIndexMap[filteredIndex]` = original `formPages` index in the form (see `getFormPageFilteredToOriginalIndices`). */
  formPageIndexMap?: readonly number[] | undefined;
};

const TOP_LEVEL: Record<string, string> = {
  name: "name",
  funnel_id: "funnelId",
  action: "action",
  is_experiment: "isExperiment",
  isExperiment: "isExperiment",
};

function snakeToCamel(segment: string): string {
  return segment.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function mapNestedPageLeaf(
  pagePrefix: "landing_page" | "confirm_page" | "thanks_page",
  leaf: string
): string {
  if (pagePrefix === "landing_page" && leaf === "has_form") {
    return "landingUrlFormIncluded";
  }
  return snakeToCamel(leaf);
}

function resolveFormPageFormikIndex(
  filteredIndex: number,
  formPageIndexMap: readonly number[] | undefined
): number {
  if (formPageIndexMap === undefined) return filteredIndex;
  const mapped = formPageIndexMap[filteredIndex];
  return mapped !== undefined ? mapped : filteredIndex;
}

/**
 * Convert server-side import error `field` strings to Formik paths under one funnel row.
 */
export function serverFieldToFormikField(
  field: string,
  options?: ServerFieldToFormikOptions
): string {
  const { formPageIndexMap } = options ?? {};

  const top = TOP_LEVEL[field];
  if (top !== undefined) return top;

  let m = /^form_pages\.(\d+)\.(.+)$/.exec(field);
  if (m) {
    const filteredIdx = parseInt(m[1], 10);
    const leaf = m[2];
    const formikIdx = resolveFormPageFormikIndex(filteredIdx, formPageIndexMap);
    return `formPages[${formikIdx}].${snakeToCamel(leaf)}`;
  }

  m = /^form_pages\[(\d+)\]\.(.+)$/.exec(field);
  if (m) {
    const filteredIdx = parseInt(m[1], 10);
    const leaf = m[2];
    const formikIdx = resolveFormPageFormikIndex(filteredIdx, formPageIndexMap);
    return `formPages[${formikIdx}].${snakeToCamel(leaf)}`;
  }

  m = /^(landing_page|confirm_page|thanks_page)\.(.+)$/.exec(field);
  if (m) {
    const pageSnake = m[1] as "landing_page" | "confirm_page" | "thanks_page";
    const leaf = m[2];
    const obj =
      pageSnake === "landing_page"
        ? "landingPage"
        : pageSnake === "confirm_page"
        ? "confirmPage"
        : "thanksPage";
    return `${obj}.${mapNestedPageLeaf(pageSnake, leaf)}`;
  }

  // Unindexed `form_pages.<leaf>` (e.g. `form_pages.url`) → first submitted form page, same as legacy `[0]` when no gaps
  m = /^form_pages\.(.+)$/.exec(field);
  if (m) {
    const leaf = m[1];
    if (!/^\d+$/.test(leaf)) {
      const formikIdx = resolveFormPageFormikIndex(0, formPageIndexMap);
      return `formPages[${formikIdx}].${snakeToCamel(leaf)}`;
    }
  }

  if (field === "landing_page") return "landingPage.url";
  if (field === "form_pages") return "formPages";
  if (field === "confirm_page") return "confirmPage.url";
  if (field === "thanks_page") return "thanksPage.url";

  return field;
}
